// The audit records that match a filter, newest first (#388): the one list the
// Audit page shows under its toolbar and a device page shows under its Audit tab.
//
// **A module of its own so the device page can import the list without importing
// the page** (#1264). It sat in `Audit.tsx`, and `devices/DeviceDetail.tsx`
// importing it from there made the Audit page eager through `Devices`, which is
// why `/audit` was the one route #739 split and vite emitted no chunk for.
import { Fragment, useState } from "react";
import { api } from "../api/client";
import { usePages } from "../api/hooks";
import { ScopeLink } from "../shell/scope";
import { Empty, Loading, ProblemBox, TableSkeleton, When } from "../ui";
import { AuditActionCell, AuditActorCell, AuditIdentifiers, AuditTargetCell, TargetRef } from "./AuditCells";

/** An event's recorded payload, as §19.4 stores it: `before` and `after` are
 * `additionalProperties: true` in the document, so what a writer put there is
 * what this shows — no field list, because the set differs per action and a
 * list here would go stale the next time a writer changes.
 *
 * **It carries no secret, and that is a property of the writers rather than of
 * this page.** §17.1: *"No secret values returned, with two exceptions"*, both
 * on their own reveal endpoints, and a reveal's own row names the actor, the
 * object and the time and never the value (ADR-0035 decision 4, ADR-0062
 * decision 4). Read across all 48 payload writers on 2026-09-20, the three that
 * touch credential-shaped fields pass either a `secrets.Sealed` — whose
 * `MarshalJSON` has no form but `{"sealed":true}` or `null`, pinned by
 * `TestSiteAdminPasswordAuditRecordsSealedAndNotTheMaterial` — or an `int`
 * count. So this renders the value verbatim rather than filtering it: a filter
 * here would be a second place to keep the secret rule, and the first one is
 * where it belongs. */
const isEmpty = (v: unknown) => v === undefined || v === null || (typeof v === "object" && Object.keys(v as object).length === 0);
const hasPayload = (e: { before?: unknown; after?: unknown }) => !isEmpty(e.before) || !isEmpty(e.after);

/** Not "nothing changed": many actions record no payload at all, and
 * ADR-0092's diagnostics row carries none of what the cell answered by
 * design. Rendering that as empty would read as missing data. */
function Payload({ before, after }: { before?: unknown; after?: unknown }) {
  if (isEmpty(before) && isEmpty(after)) return <p className="muted">This action records no payload.</p>;
  return (
    <>
      {!isEmpty(before) && <Side label="Before" value={before} />}
      {!isEmpty(after) && <Side label="After" value={after} />}
    </>
  );
}

function Side({ label, value }: { label: string; value: unknown }) {
  const entries = typeof value === "object" && value !== null && !Array.isArray(value)
    ? Object.entries(value as Record<string, unknown>) : undefined;
  return (
    <div>
      <div className="muted">{label}</div>
      {entries
        ? <dl className="kv">{entries.map(([k, v]) => (
            <Fragment key={k}><dt><code>{k}</code></dt><dd>{scalar(v)}</dd></Fragment>))}</dl>
        : <pre className="doc">{JSON.stringify(value, null, 2)}</pre>}
    </div>
  );
}

/** A scalar reads as itself; anything structured keeps its JSON, because a
 * flattened object loses which key a value sat under. */
function scalar(v: unknown) {
  if (v === null) return <span className="muted">null</span>;
  if (typeof v === "object") return <code>{JSON.stringify(v)}</code>;
  return <code>{String(v)}</code>;
}

/** The audit records that match `filters`, newest first, a page at a time —
 * the one list, whether it is the Audit page's under its toolbar or a
 * device's under its Audit tab (#388). The pages are keyed on `deps`, so a
 * new filter or another scope starts over. */
export function AuditList({ filters, deps }: { filters: { org_id?: string; customer_id?: string; site_id?: string; target_type?: string; target_id?: string }; deps: unknown[] }) {
  const q = usePages((token) => api.GET("/audit", { params: { query: { ...filters, page_size: 50, page_token: token } } }), deps);
  const items = q.items;
  const [open, setOpen] = useState<number | undefined>();
  // The header row, once for the table and for the skeleton drawn before the
  // first page arrives (#1321), so the two cannot differ.
  const head = <><th>When</th><th>Actor</th><th>Action</th><th>Target</th><th>Result</th><th>Details</th></>;
  return (
    <>
      {q.status === "loading" && !q.data && <><Loading srOnly /><TableSkeleton label="Audit events" head={head} /></>}
      {q.status === "error" && <ProblemBox problem={q.problem} />}
      {q.data && items.length === 0 && <Empty title="No audit events match." info="Changes you make appear here with who made them and what changed." />}
      {/* §19.4 records a switch apply against the generated configuration, not
          against the device, so a device-scoped read does not reach it. Said
          here rather than worked around: making this filter follow the join
          would be a new `/v1` capability, and the UI may call nothing the
          document does not expose. */}
      {filters.target_type === "device" && items.length > 0 && (
        <p className="muted" style={{ marginTop: "var(--sp-2)" }}>
          A configuration apply is recorded against the generated configuration it sent, not against the
          device, so it does not appear in this list. Filter on <code>generated_config</code> to read one.
        </p>
      )}
      {/* How many are drawn, and whether the record holds more (UX-020): the list pages at the server, a
          page of 50 at a time, and said nothing about how far it went. */}
      {items.length > 0 && <p className="pager muted" aria-live="polite">{items.length} {items.length === 1 ? "event" : "events"} shown{q.hasMore ? ", newest first; older events load with Load more" : ""}</p>}
      {items.length > 0 && (
        <div className="table-wrap"><table aria-label="Audit events">
          <thead><tr>{head}</tr></thead>
          <tbody>{items.map((e) => (
            <Fragment key={e.id}>
              <tr>
                <td><When value={e.created_at} /></td>
                <td><AuditActorCell event={e} /></td>
                <td><AuditActionCell action={e.action} /></td>
                {/* The target narrows the Audit page to itself; a device also opens on the Devices page, since its id is its serial. */}
                <td>
                  <TargetRef event={e}><ScopeLink to="/audit" facets={{ target_type: e.target_type, target_id: e.target_id }} title="Only this target"><AuditTargetCell event={e} /></ScopeLink></TargetRef>
                  {e.target_type === "device" && <>{" · "}<ScopeLink to="/operate/devices" facets={{ serial: e.target_id }} title="Open the device">Open</ScopeLink></>}
                </td>
                <td>{e.result === "ok" ? e.result : <span style={{ color: "var(--status-critical)" }}>{e.result}{e.reason_code ? ` (${e.reason_code})` : ""}</span>}</td>
                {/* The identifiers support and a log key on, and the payload,
                    are the row's detail (UX review 2026-11-01, UX-003): every
                    row has one, since every row has identifiers. */}
                <td>
                  <button className="btn btn-quiet" aria-expanded={open === e.id} aria-controls={`payload-${e.id}`}
                    onClick={() => setOpen(open === e.id ? undefined : e.id)}>
                    {open === e.id ? "Hide" : hasPayload(e) ? "Show changes" : "Show details"}
                  </button>
                </td>
              </tr>
              {open === e.id && (
                <tr id={`payload-${e.id}`}>
                  <td colSpan={6}><AuditIdentifiers event={e} /><Payload before={e.before} after={e.after} /></td>
                </tr>
              )}
            </Fragment>))}</tbody>
        </table></div>
      )}
      {q.moreProblem && <ProblemBox problem={q.moreProblem} />}
      {q.hasMore && <div className="toolbar" style={{ marginTop: "var(--sp-3)" }}><button className="btn" disabled={q.loadingMore} onClick={q.more}>Load more</button></div>}
    </>
  );
}
